
import React, { useRef, useState } from 'react';
import { ExportFormat, PMode } from '../types';

interface HeaderProps {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  download: () => void;
  clear: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onImport: (file: File) => void;
  importWeights: number[] | null;
  onWeightChange: (idx: number, val: number) => void;
  onWeightCommit: () => void;
  palette: string[];
  exportFormat: ExportFormat;
  setExportFormat: (format: ExportFormat) => void;
  currentPMode: PMode;
  setPMode: (mode: PMode) => void;
}

const Header: React.FC<HeaderProps> = ({ 
  undo, 
  redo, 
  canUndo, 
  canRedo, 
  download, 
  clear, 
  onCopy,
  onPaste,
  onImport,
  importWeights,
  onWeightChange,
  onWeightCommit,
  palette,
  exportFormat,
  setExportFormat,
  currentPMode,
  setPMode
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showPModeMenu, setShowPModeMenu] = useState(false);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport(file);
      e.target.value = '';
    }
  };

  return (
    <header className="relative bg-[#111] border-b-4 border-[#000] p-4 flex items-center justify-between z-50">
      {!importWeights && (
        <div className="flex items-center gap-4 shrink-0">
          <div className="relative">
            <button 
              onClick={() => setShowPModeMenu(!showPModeMenu)}
              className="text-3xl font-bold text-yellow-500 tracking-widest drop-shadow-md hover:text-yellow-400 flex items-center gap-2"
            >
              {currentPMode} ▾
            </button>
            {showPModeMenu && (
              <div className="absolute left-0 top-full mt-2 bg-[#222] border-2 border-[#000] shadow-lg z-50 flex flex-col min-w-[150px]">
                {Object.values(PMode).map((mode) => (
                  <button
                    key={mode}
                    className={`px-4 py-2 text-left hover:bg-gray-700 text-xl font-bold tracking-widest ${currentPMode === mode ? 'text-yellow-500' : 'text-white'}`}
                    onClick={() => {
                      setPMode(mode);
                      setShowPModeMenu(false);
                    }}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Middle Section: Sliders */}
      <div className="flex-1 mx-4 flex justify-center items-center gap-4 min-w-0 overflow-hidden">
        {importWeights && (
          <div className="flex-1 flex items-center gap-4 bg-black bg-opacity-40 p-2 px-4 border-x border-gray-800 rounded-lg min-w-0">
            <span className="text-xs text-blue-400 font-bold uppercase tracking-tighter whitespace-nowrap shrink-0">IMPORT BIAS:</span>
            {importWeights.slice(0, palette.length).map((weight, idx) => (
              <div key={idx} className="flex flex-col items-center flex-1 min-w-[60px] gap-1">
                <div className="flex items-center gap-2 w-full justify-between">
                  <div className="w-4 h-4 border border-gray-600 rounded-sm shrink-0" style={{backgroundColor: palette[idx]}} />
                  <span className="text-[10px] text-gray-300 font-mono">{weight > 0 ? '+' : ''}{weight.toFixed(2)}</span>
                </div>
                <input 
                  type="range"
                  min="-3"
                  max="3"
                  step="0.05"
                  value={weight}
                  onChange={(e) => onWeightChange(idx, parseFloat(e.target.value))}
                  onMouseUp={onWeightCommit}
                  className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-white"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 shrink-0">
        <button 
          onClick={undo}
          disabled={!canUndo}
          className={`retro-button px-4 py-1 text-xl ${canUndo ? 'bg-gray-200 text-black' : 'bg-gray-600 text-gray-400 opacity-50'}`}
        >
          UNDO
        </button>
        <button 
          onClick={redo}
          disabled={!canRedo}
          className={`retro-button px-4 py-1 text-xl ${canRedo ? 'bg-gray-200 text-black' : 'bg-gray-600 text-gray-400 opacity-50'}`}
        >
          REDO
        </button>
        <button 
          onClick={clear}
          className="retro-button px-4 py-1 bg-red-600 text-white text-xl hover:bg-red-500"
        >
          CLEAR
        </button>
        <button 
          onClick={onCopy}
          className="retro-button px-4 py-1 bg-purple-600 text-white text-xl hover:bg-purple-500"
        >
          COPY
        </button>
        <button 
          onClick={onPaste}
          className="retro-button px-4 py-1 bg-purple-600 text-white text-xl hover:bg-purple-500"
        >
          PASTE
        </button>
        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          accept="image/png, image/jpeg, image/gif, .c, .asm, .txt, text/plain"
          onChange={handleFileChange}
        />
        <button 
          onClick={handleImportClick}
          className="retro-button px-4 py-1 bg-blue-600 text-white text-xl hover:bg-blue-500"
        >
          IMPORT
        </button>
        <button 
          onClick={download}
          className="retro-button px-4 py-1 bg-green-600 text-white text-xl hover:bg-green-500"
        >
          EXPORT
        </button>
        <div className="relative">
          <button 
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="retro-button px-2 py-1 bg-green-800 text-white text-xl hover:bg-green-700 min-w-[60px]"
          >
            {exportFormat} ▾
          </button>
          {showExportMenu && (
            <div className="absolute right-0 top-full mt-2 bg-[#222] border-2 border-[#000] shadow-lg z-50 flex flex-col min-w-[80px]">
              {Object.values(ExportFormat).map((format) => (
                <button
                  key={format}
                  className={`px-4 py-2 text-left hover:bg-gray-700 text-xl ${exportFormat === format ? 'text-yellow-500' : 'text-white'}`}
                  onClick={() => {
                    setExportFormat(format);
                    setShowExportMenu(false);
                  }}
                >
                  {format}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
